import type { EstadoPortaOficina, IdZonaOperacaoPorta } from '../../dominio/didatico/oficinaCorte'
import type { EstadoTampaOficina, IdZonaOperacaoTampa } from '../../dominio/didatico/oficinaPortaMalas'
import type {
  EstadoTerceiraPortaOficina,
  IdZonaOperacaoTerceiraPorta,
} from '../../dominio/didatico/oficinaTerceiraPorta'

/**
 * O que já aconteceu em cada ponto de operação.
 *
 * Os marcadores eram todos iguais do começo ao fim do exercício: o aluno
 * cortava uma dobradiça, a peça sumia de dentro da porta (atrás da chapa, fora
 * de vista) e a cena continuava idêntica. Traduzir o estado do domínio para
 * três leituras — por fazer, mexido, concluído — é o que faz o próprio
 * marcador registrar o que a ferramenta fez ali.
 */
export type EstadoZonaOperacao = 'pendente' | 'trabalhada' | 'concluida'

export function estadoZonasPorta(
  estado: EstadoPortaOficina,
): Record<IdZonaOperacaoPorta, EstadoZonaOperacao> {
  const vinculo = (id: keyof EstadoPortaOficina['vinculos']): EstadoZonaOperacao =>
    estado.vinculos[id] === 'liberado'
      ? 'concluida'
      : estado.vinculos[id] === 'deformado' ? 'trabalhada' : 'pendente'

  return {
    chapa: estado.chapa === 'desfolhada'
      ? 'concluida'
      : estado.chapa === 'deformada' ? 'trabalhada' : 'pendente',
    macaneta: estado.tecnicaCriacaoEspaco === 'compressaoMacaneta' ? 'concluida' : 'pendente',
    trava: vinculo('trava'),
    dobradicaSuperior: vinculo('dobradicaSuperior'),
    dobradicaInferior: vinculo('dobradicaInferior'),
    limitador: vinculo('limitador'),
    fiacao: vinculo('fiacao'),
    esquadria: estado.esquadria === 'deformada' ? 'concluida' : 'pendente',
    paralama: estado.paralama === 'deformado' ? 'concluida' : 'pendente',
  }
}

export function estadoZonasTampa(
  estado: EstadoTampaOficina,
): Record<IdZonaOperacaoTampa, EstadoZonaOperacao> {
  const vinculo = (id: keyof EstadoTampaOficina['vinculos']): EstadoZonaOperacao =>
    estado.vinculos[id] === 'liberado' ? 'concluida' : 'pendente'

  return {
    chapa: estado.chapa === 'deformada' ? 'trabalhada' : 'pendente',
    trava: vinculo('trava'),
    dobradicaEsquerda: vinculo('dobradicaEsquerda'),
    dobradicaDireita: vinculo('dobradicaDireita'),
    apoio: vinculo('apoio'),
  }
}

export function estadoZonasTerceiraPorta(
  estado: EstadoTerceiraPortaOficina,
): Record<IdZonaOperacaoTerceiraPorta, EstadoZonaOperacao> {
  const lado = (id: keyof EstadoTerceiraPortaOficina['lados']): EstadoZonaOperacao =>
    estado.lados[id] === 'cortado' ? 'concluida' : 'pendente'

  return {
    superior: lado('superior'),
    inferior: lado('inferior'),
    traseiro: lado('traseiro'),
  }
}
